import type { ECharts, ECEventDefinition, ElementEvent } from 'echarts';
import type GlobalModel from 'echarts/types/src/model/Global';
import type ComponentView from 'echarts/types/src/view/Component';
import type ExtensionAPI from 'echarts/types/src/core/ExtensionAPI';
import EventEmitter, { type Query, type Option } from './EventEmitter';
import type StandardChart from './StandardChart';
import type { Option as ChartOption } from './option';

/**
 * 自定义事件
 *
 * ! 为了与 echarts 事件做区分，类型命名以 dv: 前缀，且与 echarts 保持一致全部小写
 */
type CustomEventDefinition = {
  'dv:click': (event: unknown) => void;

  // lifecycle
  'dv:beforeinit': () => void;
  'dv:afterinit': () => void;
  'dv:beforeupdate': () => void;
  'dv:afterupdate': () => void;
  'dv:beforedestroy': () => void;
  'dv:destroy': () => void;
  'dv:afterdestroy': () => void;

  // animation
  'dv:play': () => void;

  // events
  /**
   * 可用来监听 axisPointer 的坐标更新
   *
   * * 与直接使用 `echartsIns.on('updateAxisPointer')` 的区别是在有 `dataZoom.inside` 存在时，如果触发 dataZoom 的交互则不会触发该事件
   * * 调用 `echartsIns.dispatchAction({ type: 'updateAxisPointer' })` 时也会触发
   */
  'dv:updateAxisPointer': (...args: unknown[]) => void;
};

// FIXME: 代码提示失败
// type EventDefinition = CustomEventDefinition & ECEventDefinition;
type EventDefinition = CustomEventDefinition;

/**
 * 事件处理
 */
export default class EventHandler extends EventEmitter<EventDefinition> {
  /** 在 echarts 未实例化之前用户传入的绑定事件 */
  private __pendingToRegisterEChartsInsEvents: ((ecIns: ECharts) => void)[] = [];

  /** `dataZoom.inside` 组件正在交互中 */
  private __DATA_ZOOM_INSIDE_INTERACTING = false;

  /**
   * 注册事件
   *
   * @example
   * ```js
   * on(eventType, handler, option? | context)
   * on(eventType, query, handler, option? | context)
   * ```
   */
  on<
    K extends keyof EventDefinition,
    ARG1 extends EventDefinition[K] | Query<EventDefinition[K]>,
    ARG2 = ARG1 extends EventDefinition[K] ? Option : EventDefinition[K],
    ARG3 = ARG2 extends Option ? never : Option
  >(eventType: K, arg1: ARG1, arg2?: ARG2, arg3?: ARG3) {
    // * 委托给 echarts 实例
    if (!String(eventType).startsWith('dv:')) {
      const echartsIns = this.__echarts as ECharts;

      if (echartsIns) {
        echartsIns.on(eventType, arg1, arg2, arg3);
      } else {
        this.__pendingToRegisterEChartsInsEvents.push(ecIns => {
          ecIns.on(eventType, arg1, arg2, arg3);
        });
      }

      return;
    }

    super.on(eventType, arg1, arg2, arg3);
  }

  /**
   * 注册事件（仅触发一次）
   *
   * @example
   * ```js
   * once(eventType, handler, option? | context)
   * once(eventType, query, handler, option? | context)
   * ```
   */
  once<
    K extends keyof EventDefinition,
    ARG1 extends EventDefinition[K] | Query<EventDefinition[K]>,
    ARG2 = ARG1 extends EventDefinition[K] ? Option : EventDefinition[K],
    ARG3 = ARG2 extends Option ? never : Option
  >(eventType: K, arg1: ARG1, arg2?: ARG2, arg3?: ARG3) {
    // * 委托给 echarts 实例
    if (!String(eventType).startsWith('dv:')) {
      const self = this;
      const echartsIns = self.__echarts as ECharts;

      if (typeof arg2 === 'function') {
        const originHandler = arg2;
        arg2 = function handler(...args) {
          if (self.__echarts) {
            originHandler.call(this, ...args);
            (self.__echarts as ECharts).off(eventType, handler);
          }
        };
      } else if (typeof arg1 === 'function') {
        const originHandler = arg1;
        arg1 = function handler(...args) {
          if (self.__echarts) {
            originHandler.call(this, ...args);
            (self.__echarts as ECharts).off(eventType, handler);
          }
        };
      }

      if (echartsIns) {
        echartsIns.on(eventType, arg1, arg2, arg3);
      } else {
        this.__pendingToRegisterEChartsInsEvents.push(ecIns => {
          ecIns.on(eventType, arg1, arg2, arg3);
        });
      }

      return;
    }

    super.once(eventType, arg1, arg2, arg3);
  }

  off<K extends keyof EventDefinition>(eventType: K, handler?: EventDefinition[K]) {
    // * 委托给 echarts 实例
    if (!String(eventType).startsWith('dv:')) {
      const echartsIns = this.__echarts as ECharts;
      if (echartsIns) {
        echartsIns.off(eventType, handler);
      }

      return;
    }

    super.off(eventType, handler);
  }

  emit<K extends keyof EventDefinition>(eventType: K, ...args: Parameters<EventDefinition[K]>) {
    // * 委托给 echarts 实例
    if (!String(eventType).startsWith('dv:')) {
      const echartsIns = this.__echarts as ECharts;
      if (echartsIns) {
        echartsIns.trigger(eventType as string, ...args);
      }

      return;
    }

    super.emit(eventType, ...args);
  }

  /**
   * 事件绑定
   *
   * ! 由子类完成初始化后调用
   */
  protected _bindEvents() {
    const self = this as unknown as StandardChart;
    const echartsIns = self.getECharts();
    const zrIns = echartsIns.getZr();

    echartsIns.on('updateAxisPointer', (...args) => {
      if (!self.__DATA_ZOOM_INSIDE_INTERACTING) {
        self.emit('dv:updateAxisPointer', ...args);
      }
    });

    zrIns.on('click', event => {
      self.emit('dv:click', event);
    });

    self.__handleInteraction();
  }

  private __handleInteraction() {
    // 图表与页面的交互 ------------------------------------------------------------------
    this.__handlePageScroll();

    // 图表内交互 ------------------------------------------
    this.__handleTooltip();
  }

  /**
   * * 处理图表和页面的滚动交互冲突 / dataZoom 与 tooltip 交互冲突 问题
   */
  // eslint-disable-next-line max-lines-per-function
  private __handlePageScroll() {
    const self = this as unknown as StandardChart;
    const echartsIns = self.getECharts();
    const zrIns = echartsIns.getZr();

    /** 用来记录是否触发了图表内的交互 */
    let chartInteraction;
    /** 记录交互类型 */
    let interactionType: '' | 'pageScrollX' | 'pageScrollY' | 'dataZoom' = '';
    let mousedownPoint = [null, null];
    let pendingMousedownTimer: number = null;
    /** mousemove 触发后等待 mousedown 触发的时间 */
    const MOUSEMOVE_PENDING_MOUSEDOWN = 50;
    let delayEvents: (() => void)[] = [];
    /** 等待触发图表内交互的定时器 */
    let pendingChartInteractionTimer: number;
    /** 用来判断用户触发图表交互的延迟时间 */
    const DELAY = 3e2;
    // 用来判断用户意图为竖向滚动页面的手指滑动距离阈值
    const DISTANCE_TRIGGER_PAGE_TO_SCROLL_Y = 10;
    let mouseUpCallback: (() => void)[] = [];

    const originTrigger = zrIns.handler.trigger.bind(zrIns.handler);

    // eslint-disable-next-line complexity, sonarjs/cognitive-complexity, max-lines-per-function
    zrIns.handler.trigger = (...args) => {
      if (args[1]) {
        // ! 获取触发的交互类型
        args[1].$dvGetInteractionType = () => interactionType;
      }

      if (
        !echartsIns.getModel() ||
        (echartsIns.getModel().option as ChartOption).dvInteraction?.disablePageScroll
      ) {
        originTrigger(...args);

        return;
      }

      // ! 判断交互点所在的组件，目的是为了解决交互在 dataZoom 等组件上触发时不需要执行页面滚动冲突检测逻辑以提高用户交互响应性
      const interactingCompModel = (echartsIns._api as ExtensionAPI).getComponentByElement(
        args[1]?.target
      );

      if (
        !chartInteraction &&
        (args[0] === 'mousedown' || args[0] === 'mousemove' || args[0] === 'pinch') &&
        (!interactingCompModel || interactingCompModel?.mainType !== 'dataZoom')
      ) {
        if (interactionType === 'pageScrollX' || interactionType === 'pageScrollY') {
          // 上下移动点击，取消浮层和光标
          echartsIns.dispatchAction({ type: 'dvImmediatelyHideTip' });
          echartsIns.dispatchAction({ type: 'updateAxisPointer' });
          return;
        }

        self.__DATA_ZOOM_INSIDE_INTERACTING = true;

        // ! 由于触发事件的顺序是 mousemove => mousedown => mousemove => mouseup
        // ! 所以，判断 mousemove 触发后一定时间内没触发 mousedown 就认为是 PC 的鼠标移动事件，而不是移动端的事件，放弃对事件的拦截
        if (
          args[0] === 'mousemove' &&
          mousedownPoint[0] === null &&
          pendingMousedownTimer === null
        ) {
          pendingMousedownTimer = setTimeout(() => {
            pendingMousedownTimer = null;
            chartInteraction = true;
            self.__DATA_ZOOM_INSIDE_INTERACTING = false;
            interactionType = '';
          }, MOUSEMOVE_PENDING_MOUSEDOWN) as unknown as number;
        }

        if (args[0] === 'mousedown') {
          if (mousedownPoint[0] !== null) {
            return;
          }

          // * 此时触发了移动端的触摸滑动事件
          if (pendingMousedownTimer) {
            clearTimeout(pendingMousedownTimer);
            pendingMousedownTimer = null;
          }

          mousedownPoint = [args[1].offsetX, args[1].offsetY] as [number, number];

          // ! 用户按住不移动时超过一定时间可认为触发了图表内交互，且要将之前被拦截的事件重新触发一次

          pendingChartInteractionTimer = setTimeout(() => {
            if (!self.getECharts()) {
              return;
            }

            pendingChartInteractionTimer = null;

            if (interactionType !== '') {
              return;
            }

            (echartsIns.getModel() as GlobalModel).eachComponent((compType, compModel) => {
              if (compType !== 'dataZoom' && compModel.subType !== 'inside') {
                return;
              }

              const originOption = {
                disabled: compModel.option.disabled,

                moveOnMouseMove: compModel.option.moveOnMouseMove
              };

              // * 先将 dataZoom.inside 禁止

              compModel.mergeOption({ disabled: true }, echartsIns.getModel() as GlobalModel);

              // * 手指松开的时候再开启
              // ! 有可能在交互过程中用户触发 setOption 更改了 dataZoom.inside 的配置，但暂时忽略掉这种情况
              mouseUpCallback.push(() => {
                compModel.mergeOption({ ...originOption }, echartsIns.getModel() as GlobalModel);
              });
            });

            chartInteraction = true;
            self.__DATA_ZOOM_INSIDE_INTERACTING = false;
            interactionType = '';
            delayEvents.forEach(func => func());
            delayEvents = [];
          }, DELAY) as unknown as number;
        }

        // ! 利用手指滑动距离判断是不是触发竖向滑动交互
        if (
          args[0] === 'mousemove' &&
          mousedownPoint[1] !== null &&
          Math.abs(args[1].offsetY - mousedownPoint[1]) > DISTANCE_TRIGGER_PAGE_TO_SCROLL_Y
        ) {
          clearTimeout(pendingChartInteractionTimer);
          pendingChartInteractionTimer = null;

          // * 确认为页面的竖向滚动操作
          interactionType = 'pageScrollY';
          self.__DATA_ZOOM_INSIDE_INTERACTING = false;

          return;
        }

        // ! 利用手指滑动距离判断是不是触发横向滑动交互，进而确定是页面横向滚动还是 dataZoom.inside 的交互
        if (
          (args[0] === 'mousemove' || args[0] === 'pinch') &&
          mousedownPoint[0] !== null &&
          Math.abs(args[1].offsetX - mousedownPoint[0]) > DISTANCE_TRIGGER_PAGE_TO_SCROLL_Y
        ) {
          clearTimeout(pendingChartInteractionTimer);
          pendingChartInteractionTimer = null;

          // 隐藏
          echartsIns.dispatchAction({ type: 'dvImmediatelyHideTip' });
          echartsIns.dispatchAction({ type: 'updateAxisPointer' });

          if ((echartsIns.getModel().option as ChartOption).dvInteraction?.enablePageScrollX) {
            // * 确认为页面的横向滚动操作
            interactionType = 'pageScrollX';
            self.__DATA_ZOOM_INSIDE_INTERACTING = false;

            return;
          }

          // * 确认为 dataZoom.inside 操作
          if (
            (echartsIns.getModel() as GlobalModel).findComponents({
              mainType: 'dataZoom',
              subType: 'inside'
            }).length
          ) {
            (echartsIns.getModel() as GlobalModel).eachComponent((compType, compModel) => {
              if (compType !== 'tooltip' && compType.search(/axis/i) === -1) {
                return;
              }

              const view = echartsIns.getViewOfComponentModel(compModel) as ComponentView;

              // * 先将 tooltip 和 axisPointer 禁止
              if (compType === 'tooltip') {
                view.__$originShowMethod = view._tooltipContent.show;

                view._tooltipContent.show = () => {};

                view._tooltipContent.hide();
              } else if (view._axisPointer) {
                view.__$originRenderMethod = view._axisPointer.render;

                view._axisPointer.render = () => {};
              }
              const time = Date.now();

              view.__$pendingReset = time;

              // * 手指松开的时候再开启
              mouseUpCallback.push(() => {
                if (view.__$pendingReset !== time) {
                  return;
                }

                if (compType === 'tooltip') {
                  view._tooltipContent.show = view.__$originShowMethod;
                } else if (view._axisPointer) {
                  view._axisPointer.render = view.__$originRenderMethod;
                }

                // 隐藏
                echartsIns.dispatchAction({ type: 'dvImmediatelyHideTip' });
                echartsIns.dispatchAction({ type: 'updateAxisPointer' });
              });
            });

            interactionType = 'dataZoom';
            chartInteraction = true;
            delayEvents.forEach(func => func());
            delayEvents = [];

            return;
          }
        }

        if (!chartInteraction) {
          delayEvents.push(() => {
            originTrigger(...args);
          });

          return;
        }
      }

      if (args[0] === 'mousemove' && (args[1]?.event as MouseEvent)?.cancelable) {
        (args[1]?.event as MouseEvent)?.preventDefault();
      }

      originTrigger(...args);

      if (args[0] === 'mouseup') {
        interactionType = '';
        chartInteraction = false;
        self.__DATA_ZOOM_INSIDE_INTERACTING = false;
        mousedownPoint = [null, null];
        delayEvents = [];

        if (pendingChartInteractionTimer) {
          clearTimeout(pendingChartInteractionTimer);
          pendingChartInteractionTimer = null;
        }

        mouseUpCallback.forEach(cb => cb());
        mouseUpCallback = [];
      }

      //
    };
  }

  /**
   * * 处理 tooltip、axisPointer 的显示隐藏逻辑
   *
   * - 移动端交互，手指离开屏幕时 Tooltip 和 AxisPointer 组件开始自动消失
   */
  private __handleTooltip() {
    const self = this as unknown as StandardChart;
    const echartsIns = self.getECharts();
    const zrIns = echartsIns.getZr();

    let handleAutoHideTimers: number[] = [];
    const TIMER_DELAY = 1e2;
    function cancelHandleAutoHide() {
      handleAutoHideTimers.forEach(timer => clearTimeout(timer));
      handleAutoHideTimers = [];
    }

    function handleAutoHide(ecOption) {
      cancelHandleAutoHide();

      // ! 需要一定的延迟，因为立即调用的话还没渲染完成，会被覆盖
      handleAutoHideTimers.push(
        setTimeout(() => {
          if (!self.getECharts()) {
            return;
          }

          if ((ecOption?.tooltip || []).some(opt => opt.dvAutoHide === true)) {
            echartsIns.dispatchAction({ type: 'hideTip' });
          }

          if ((ecOption?.axisPointer || []).some(opt => opt.dvAutoHide === true)) {
            handleAutoHideTimers.push(
              setTimeout(() => {
                if (!self.getECharts()) {
                  return;
                }

                // ? 如果有多根轴
                // 隐藏
                echartsIns.dispatchAction({ type: 'updateAxisPointer' });
              }, Math.max(...(ecOption?.axisPointer || []).map(opt => (opt?.dvHideDelay as number) || 0), TIMER_DELAY + 1) - TIMER_DELAY)
            );
          }
        }, TIMER_DELAY)
      );
    }

    // 自动延迟隐藏 tooltip -----------------------------------------------------------------
    zrIns
      .on('mousedown', event => {
        cancelHandleAutoHide();
        const interactingCompModel = (echartsIns._api as ExtensionAPI).getComponentByElement(
          event.target
        );
        // 点击type为slider的dataZoom
        if (interactingCompModel?.type === 'dataZoom.slider') {
          // 隐藏
          echartsIns.dispatchAction({ type: 'dvImmediatelyHideTip' });
          echartsIns.dispatchAction({ type: 'updateAxisPointer' });
        }
      })
      .on('mouseup', () => {
        const ecOption = echartsIns.getOption();

        handleAutoHide(ecOption);
      })
      .on('click', () => {
        cancelHandleAutoHide();

        const ecOption = echartsIns.getOption();

        handleAutoHide(ecOption);
      });

    // 主动隐藏 tooltip -----------------------------------------------------------------
    zrIns
      .on('click', event => {
        const ecOption = echartsIns.getOption();

        // * 点击画布空白处、click 不触发交互时 让 tooltip 和 axisPointer 隐藏
        if (
          !event.target ||
          (ecOption?.axisPointer || []).some(
            opt => typeof opt.triggerOn !== 'undefined' && opt.triggerOn !== 'click'
          )
        ) {
          // 隐藏
          echartsIns.dispatchAction({ type: 'dvImmediatelyHideTip' });
          // TODO 这段代码注释掉，也能主动隐藏axisPointer，如果不注释掉会和echarts产生冲突————axisPointer的status和视图不一致
          // echartsIns.dispatchAction({ type: 'updateAxisPointer' });
        }
      })
      .on('globalout', () => {
        echartsIns.dispatchAction({ type: 'dvImmediatelyHideTip' });
      });

    // 由于globalout在短时间内不会立即触发，手动监听图表外点击，立即同时隐藏axisPointer和tooltip
    function handler(event: MouseEvent) {
      if (!self.getECharts()) {
        window.removeEventListener('click', handler);
        return;
      }

      const dom = self.getDom();
      let isInner = false;
      let $targetEl = event.target as HTMLElement;

      do {
        if ($targetEl === dom) {
          isInner = true;
          break;
        }
        $targetEl = $targetEl?.parentElement;
      } while ($targetEl && $targetEl !== document.body);

      if (!isInner) {
        // 隐藏
        echartsIns.dispatchAction({ type: 'dvImmediatelyHideTip' });
        echartsIns.dispatchAction({ type: 'updateAxisPointer' });
      }
    }
    window.addEventListener('click', handler);
    // 主动隐藏 tooltip -----------------------------------------------------------------

    //
  }

  /**
   * 刷新等待注册到 echarts 实例的事件
   *
   * ! 由子类完成 echarts 初始化后调用
   */
  protected _flushPendingToRegisterEChartsInsEvents() {
    const echartsIns = this.__echarts as ECharts;

    this.__pendingToRegisterEChartsInsEvents.forEach(register => {
      register(echartsIns);
    });

    this.__pendingToRegisterEChartsInsEvents.length = 0;
  }

  /**
   * 分发事件
   */
  dispatchEvent<E extends keyof EventDefinition>(
    eventType: E,
    ...args: Parameters<EventDefinition[E]>
  ) {
    // TODO
    this.emit(eventType, ...args);
  }

  //
}
